// CallNow.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';

/* ──────────── ANIMATIONS ──────────── */

// Fade-in for “Copied!” text
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ──────────── TEXT & NUMBER STYLES ──────────── */

// “24/7 SUPPORT” label; hidden prop removes it during hover
const SupportLabel = styled.span`
  display: ${({ hidden }) => (hidden ? 'none' : 'inline-block')};
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Base styling for phone numbers
const baseNumberStyles = css`
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
`;

// Desktop-only phone number: auto-copy on hover, and “Copied!” feedback
const DesktopNumber = styled.span`
  ${baseNumberStyles};
  font-size: 1rem; /* slightly larger font */
  cursor: pointer;
  position: relative;
  user-select: none;
  transition: color 0.3s ease;

  /* Underline on parent hover */
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: #ffffff;
    transition: width 0.3s ease;
  }
  ${({ parentHovered }) =>
    parentHovered &&
    css`
      &::after {
        width: 100%;
      }
    `}

  /* “Copied!” color + fade-in */
  &.active {
    color: #d8b4fe;
    animation: ${fadeIn} 0.3s ease;
  }
`;

// Mobile-only phone number (static)
const MobileNumber = styled.span`
  ${baseNumberStyles};
  font-size: 1rem; /* match desktop font size */
  user-select: none;
`;

/* ──────────── “Talk to David in any language” LINE ──────────── */

// Smaller, appears above the number when visible
const TalkLine = styled.span`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.2;
  color: #ffffff;
  margin-bottom: 4px;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

/* ──────────── LAYOUT CONTAINERS ──────────── */

// Centers TalkLine, SupportLabel, and phone number
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

/* ──────────── BUTTON & CONTAINER ──────────── */

// Sticks the button to the bottom-left of the viewport
const Container = styled.div`
  position: fixed;
  bottom: 1.5rem; /* Tailwind’s “bottom-6” */
  left: 1.5rem;   /* Tailwind’s “left-6” */
  z-index: 9999;
  pointer-events: all;

  @media (min-width: 768px) {
    bottom: 1.5rem;
    left: 1.5rem;
  }
  @media (max-width: 480px) {
    bottom: 1.5rem;
    left: 1.5rem;
  }
`;

// The main button: calling on click, auto-copy on hover (desktop only)
const CallButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  width: 200px;     /* increased from 180 → 200 */
  height: 65px;     /* increased from 55 → 65 */
  background: linear-gradient(45deg, #6b21a8, #3b82f6);
  border: none;
  border-radius: 34px; /* slightly larger radius */
  color: #ffffff;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  cursor: pointer;
  overflow: hidden;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.25),
    0 3px 8px rgba(0, 0, 0, 0.15),
    inset 0 -2px 4px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease,
    width 0.3s ease,
    height 0.3s ease;

  &:focus {
    outline: 3px solid rgba(255, 255, 255, 0.7);
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(2px);
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.2),
      0 2px 6px rgba(0, 0, 0, 0.1),
      inset 0 -1px 2px rgba(0, 0, 0, 0.1);
  }

  /* DESKTOP-ONLY: Expand on hover, hide SUPPORT, show TalkLine */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      width: 300px;   /* increased from 260 → 300 */
      height: 85px;   /* increased from 75 → 85 */
      transform: translateY(-4px);
      box-shadow:
        0 8px 20px rgba(0, 0, 0, 0.3),
        0 5px 12px rgba(0, 0, 0, 0.2),
        inset 0 -2px 4px rgba(0, 0, 0, 0.1);

      & ${SupportLabel} {
        display: none;
      }
    }
  }
`;

// Phone icon container
const IconContainer = styled.div`
  flex-shrink: 0;
  width: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 10px; /* slightly more padding */
`;

// SVG icon for the phone
const PhoneIcon = styled.svg`
  width: 28px;  /* increased from 24 → 28 */
  height: 28px; /* increased from 24 → 28 */
  fill: currentColor;
  transition: transform 0.3s ease;

  ${CallButton}:hover & {
    transform: scale(1.1);
  }
`;

// Wraps the right-side content
const TextWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-right: 10px; /* slightly more padding */
`;

/* ──────────── MAIN COMPONENT ──────────── */

const CallNow = () => {
  const phoneNumber = '647-484-3886';
  const rawNumber = phoneNumber.replace(/-/g, '');

  // Detect desktop vs. mobile
  const [isDesktop, setIsDesktop] = useState(false);
  // “Copied!” feedback toggle
  const [copied, setCopied] = useState(false);
  // Whether the button is hovered (desktop only)
  const [hovered, setHovered] = useState(false);

  // Ref to store the auto-copy timer
  const copyTimerRef = useRef(null);

  useEffect(() => {
    const mql = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setIsDesktop(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Copy the number to clipboard
  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(phoneNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [phoneNumber]);

  // On click (desktop or mobile), always call
  const handleCallNumber = useCallback(() => {
    window.location.href = `tel:${rawNumber}`;
  }, [rawNumber]);

  // Top-level click
  const handleClick = (e) => {
    e.stopPropagation();
    handleCallNumber();
  };

  // Start 250 ms timer to auto-copy when mouse enters the number
  const handleNumberMouseEnter = () => {
    if (!isDesktop) return;
    copyTimerRef.current = setTimeout(() => {
      copyToClipboard();
      copyTimerRef.current = null;
    }, 250);
  };

  // Cancel auto-copy if mouse leaves early
  const handleNumberMouseLeave = () => {
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }
  };

  return (
    <Container>
      <CallButton
        onClick={handleClick}
        aria-label={`Call ${phoneNumber}`}
        onMouseEnter={() => isDesktop && setHovered(true)}
        onMouseLeave={() => isDesktop && setHovered(false)}
      >
        <IconContainer>
          <PhoneIcon viewBox="0 0 24 24" aria-hidden="true">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.24 1.02l-2.2 2.2zm1.36-4.79h-2.5c-.55 0-1 .45-1 1 0 8.28 6.72 15 15 15 .55 0 1-.45 1-1v-2.5c0-.55-.45-1-1-1-1.02 0-2.02-.15-2.98-.43l-2.83 2.83c-2.83-1.44-5.15-3.75-6.59-6.59l2.83-2.83c-.28-.96-.43-1.96-.43-2.98 0-.55-.45-1-1-1z"
            />
          </PhoneIcon>
        </IconContainer>

        <TextWrapper>
          <ContentWrapper>
            {/* Show TalkLine when hovered */}
            <TalkLine visible={hovered}>
              Talk to David in any language
            </TalkLine>

            {/* Hide SupportLabel when hovered */}
            <SupportLabel hidden={hovered}>24/7 SUPPORT</SupportLabel>

            {isDesktop ? (
              <DesktopNumber
                className={copied ? 'active' : ''}
                parentHovered={hovered}
                onMouseEnter={handleNumberMouseEnter}
                onMouseLeave={handleNumberMouseLeave}
                role="button"
                tabIndex={0}
                aria-label="Phone number (auto-copy on hover)"
              >
                {copied ? 'Copied!' : phoneNumber}
              </DesktopNumber>
            ) : (
              <MobileNumber>{phoneNumber}</MobileNumber>
            )}
          </ContentWrapper>
        </TextWrapper>
      </CallButton>
    </Container>
  );
};

export default CallNow;
